import { useAsyncRetry } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  identityApiRef,
  useApi,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { useManagedTeamWithChildren } from '../../hooks/useManagedTeamWithChildren';
import {
  fetchWatchtowerRecommendations,
  submitRevisions,
  refreshRepositories,
} from '../api';
import { ProposedOwnershipRepo, RevisionChange } from '../types';
import {
  fetchUserGroups,
  fetchGroupTitles,
  validateRepoExistsInCatalog,
  extractRepoName,
  extractTeamId,
  extractSupIds,
  fetchTeamOwnerInfo,
  getParentTeamRef,
  fetchUserEmail,
  buildRevisionRequest,
} from '../utils';

export const useProposedOwnershipRepos = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { managedTeamInfo, managedTeamLoading } = useManagedTeamWithChildren();

  const {
    value: repos,
    loading,
    error,
    retry,
  } = useAsyncRetry(async () => {
    if (managedTeamLoading) {
      return [];
    }

    // Get team IDs to query
    let groupNames: string[];
    let supIds: string[];

    if (managedTeamInfo && managedTeamInfo.allTeamRefs.length > 0) {
      groupNames = managedTeamInfo.allTeamRefs.map(ref =>
        ref.replace('group:default/', ''),
      );
      supIds = extractSupIds(groupNames);
    } else {
      const userGroups = await fetchUserGroups(identityApi, catalogApi);
      groupNames = userGroups.groupNames;
      supIds = userGroups.supIds;
    }

    if (groupNames.length === 0) {
      return [];
    }

    const watchtowerRecommendations = await fetchWatchtowerRecommendations(
      discoveryApi,
      supIds,
    );

    const groupTitles = await fetchGroupTitles(catalogApi, groupNames);

    const results: ProposedOwnershipRepo[] = [];

    for (const rec of watchtowerRecommendations) {
      const teamId = (rec.recommended_team_id || '').toLowerCase();
      const teamTitle = groupTitles[teamId] || teamId;
      const repoName = extractRepoName(rec.repository_name);

      const source: 'catalog' | 'watchtower' =
        rec.recommendation_type === null ? 'catalog' : 'watchtower';

      const catalogEntity = await validateRepoExistsInCatalog(
        catalogApi,
        repoName,
      );

      if (!catalogEntity) {
        continue;
      }

      const githubProjectSlug =
        catalogEntity.metadata.annotations?.['github.com/project-slug'] ||
        `tomtom-internal/${repoName}`;

      results.push({
        id: rec.repository_name,
        entity: catalogEntity.metadata.title || catalogEntity.metadata.name,
        entityName: catalogEntity.metadata.name,
        entitySubtext: `${catalogEntity.kind.toLowerCase()}-${
          catalogEntity.metadata.name
        }`,
        repository: githubProjectSlug,
        repositoryName: repoName,
        teamRecommendation: teamTitle,
        teamEntityRef: `group:default/${teamId}`,
        entityRef: stringifyEntityRef(catalogEntity),
        repositoryUrl: githubProjectSlug,
        source,
        proposedByEmail: rec.user_email || undefined,
        recommendationType: rec.recommendation_type || undefined,
      });
    }

    return results;
  }, [
    identityApi,
    catalogApi,
    discoveryApi,
    managedTeamInfo,
    managedTeamLoading,
  ]);

  return {
    repos: repos || [],
    loading: loading || managedTeamLoading,
    error,
    refetch: retry,
  };
};

export const useSubmitRevisions = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const submit = async (
    repositories: ProposedOwnershipRepo[],
    changes: Record<string, RevisionChange>,
  ): Promise<void> => {
    const userEmail = await fetchUserEmail(identityApi, catalogApi);
    const revisions = [];

    for (const repo of repositories) {
      const change = changes[repo.id];
      if (!change) continue;

      const originalTeamId = extractTeamId(repo.teamEntityRef);
      const repoName = extractRepoName(repo.repository);

      if (change.markAsUnknown) {
        const parentRef = await getParentTeamRef(
          catalogApi,
          repo.teamEntityRef,
        );

        if (!parentRef) {
          throw new Error(
            `Cannot escalate ${repo.entity}: The team "${repo.teamRecommendation}" does not have a parent team defined`,
          );
        }

        const parentInfo = await fetchTeamOwnerInfo(catalogApi, parentRef);
        revisions.push(
          buildRevisionRequest(
            repoName,
            userEmail,
            originalTeamId,
            parentInfo,
            'MARK_UNKNOWN',
          ),
        );
      } else if (change.teamEntityRef) {
        const newTeamInfo = await fetchTeamOwnerInfo(
          catalogApi,
          change.teamEntityRef,
        );
        revisions.push(
          buildRevisionRequest(
            repoName,
            userEmail,
            originalTeamId,
            newTeamInfo,
            'PROPOSE_TEAM',
            'Revised ownership proposal',
          ),
        );
      }
    }

    if (revisions.length === 0) {
      return;
    }

    await submitRevisions(discoveryApi, revisions);

    const repoNames = revisions.map(r => r.repository_name);
    await refreshRepositories(discoveryApi, repoNames);
  };

  return { submit };
};
